import { clientConfig } from '../client'
import { sleep } from './sleep'

/** SLOW = no answer within the timeout: a slow network, not a blocker (blockers fail at once) */
export type AdLoadStatus = 'LOADED' | 'NOT_LOADED' | 'SLOW'
export type AdDecision = 'allowed' | 'block'

/** Google IMA SDK loader */
export const IMA_SRC = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js'

/** Per-attempt load timeout - protects against false NOT_LOADED on slow networks */
export const SCRIPT_TIMEOUT = 3000
/** How many extra attempts to make after the first failure (Stage 2 - Retry) */
export const RETRY_COUNT = 3
/** Delay between retries */
export const RETRY_DELAY = 1000

export interface AdBlockResult {
  imaStatus: AdLoadStatus
  decision: AdDecision
  adblock: boolean
}

/**
 * Loads the resource as a real <script> and judges by onload/onerror.
 *
 * A <script> tag (not fetch) is used on purpose: EasyList rules for ad scripts are
 * usually typed `$script`, so a `fetch` (xmlhttprequest) bypasses them and falsely
 * reports LOADED. ima3.js is hard-blocked (no surrogate) by every ad blocker, so a
 * blocked resource fires `onerror` -> NOT_LOADED.
 */
function probe(src: string, timeout = SCRIPT_TIMEOUT): Promise<AdLoadStatus> {
  return new Promise<AdLoadStatus>(resolve => {
    const script = document.createElement('script')
    script.async = true
    let settled = false

    const finish = (status: AdLoadStatus) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timer)
      script.remove()
      resolve(status)
    }

    // on Slow 3G ima3.js takes far longer than the timeout; counting that as
    // blocked showed the ad-block screen instead of the video
    const timer = setTimeout(() => finish('SLOW'), timeout)
    script.onload = () => finish('LOADED')
    script.onerror = () => finish('NOT_LOADED')
    script.src = src
    document.head.appendChild(script)
  })
}

/**
 * Stage 1 + 2 - Google IMA SDK availability with retry.
 * Probes ima3.js, and while it reports NOT_LOADED repeats up to RETRY_COUNT times
 * (a network error may recover; a blocked one stays NOT_LOADED). SLOW is not retried.
 */
export async function checkIMA(retries = RETRY_COUNT): Promise<AdLoadStatus> {
  let status = await probe(IMA_SRC)
  let attempt = 0
  while (status === 'NOT_LOADED' && attempt < retries) {
    attempt++
    await sleep(RETRY_DELAY)
    status = await probe(IMA_SRC)
  }
  return status
}

/**
 * Stage 3 - Decision Engine.
 * Block when IMA is NOT_LOADED (after retries). Allow otherwise.
 */
export function decideAdBlock(ima: AdLoadStatus): AdDecision {
  return ima === 'NOT_LOADED' ? 'block' : 'allowed'
}

/** Remote kill-switch (from .env -> clientConfig): disables detection without a release */
export function isAdBlockDetectionEnabled(): boolean {
  return clientConfig.adblock_detection !== 'disabled'
}

/**
 * Runs the full IMA detection pipeline (Stages 1-3).
 * When detection is disabled via config, resolves to "allowed" without probing.
 */
export async function detectAdBlock(): Promise<AdBlockResult> {
  if (!isAdBlockDetectionEnabled()) {
    return { imaStatus: 'LOADED', decision: 'allowed', adblock: false }
  }
  const imaStatus = await checkIMA()
  const decision = decideAdBlock(imaStatus)
  return { imaStatus, decision, adblock: decision === 'block' }
}
